import * as Sentry from '@sentry/react';

interface LogContext {
  err?: unknown;
  [key: string]: unknown;
}

const isDev = (): boolean => import.meta.env.DEV;

const extraFrom = (
  context?: LogContext,
): Record<string, unknown> | undefined => {
  if (!context) return undefined;
  const { err, ...rest } = context;
  return Object.keys(rest).length ? rest : undefined;
};

// Centralized logging. Errors and warnings ship to Sentry; info/debug are
// dev-console only (no-ops in production builds).
export const logger = {
  error(message: string, context?: LogContext): void {
    const error = context?.err ?? new Error(message);
    Sentry.captureException(error, {
      tags: { logMessage: message },
      extra: extraFrom(context),
    });
    if (isDev()) console.error(message, context ?? '');
  },

  warn(message: string, context?: LogContext): void {
    Sentry.captureMessage(message, {
      level: 'warning',
      extra: extraFrom(context),
    });
    if (isDev()) console.warn(message, context ?? '');
  },

  info(message: string, context?: LogContext): void {
    if (isDev()) console.info(message, context ?? '');
  },

  debug(message: string, context?: LogContext): void {
    if (isDev()) console.debug(message, context ?? '');
  },
};
